import { useCallback, useMemo } from "react";

import { DashboardService } from "@/api";
import { useApi } from "@/hooks/use-api";
import { useAuth } from "@/hooks/use-auth";
import { usePoll } from "@/hooks/use-poll";
import { useTimeGreeting } from "@/hooks/use-time-greeting";
import { useTranslation } from "@/i18n";
import type { TranslationKey } from "@/i18n";

import { DashboardGreeting } from "@/components/dashboard/dashboard-greeting";
import { QuickActions } from "@/components/dashboard/quick-actions";
import { GovernancePostureCard } from "@/components/dashboard/governance-posture-card";
import { WorkspacesCard } from "@/components/dashboard/workspaces-card";
import { RequestsCard } from "@/components/dashboard/requests-card";
import { PolicyDecisionsCard } from "@/components/dashboard/policy-decisions-card";
import { RiskScoreCard } from "@/components/dashboard/risk-score-card";
import { RecentActivityCard } from "@/components/dashboard/recent-activity-card";

const POLL_INTERVAL_MS = 30_000;

const GREETING_KEY: Record<
  ReturnType<typeof useTimeGreeting>,
  TranslationKey
> = {
  morning: "dashboard.greeting.morning",
  afternoon: "dashboard.greeting.afternoon",
  evening: "dashboard.greeting.evening",
  night: "dashboard.greeting.night",
};

export default function DashboardPage() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const greetingBand = useTimeGreeting();

  // Resolve the time-of-day band to a localised phrase, then
  // append the user's first name when one is available.
  const greeting = useMemo(() => {
    const phrase = t(GREETING_KEY[greetingBand]);
    const firstName = user?.full_name?.split(" ")[0];
    return firstName ? `${phrase}, ${firstName}` : phrase;
  }, [t, greetingBand, user?.full_name]);

  // The dashboard scopes metrics to the active workspace. ``user``
  // is guaranteed by ``ProtectedRoute`` but ``workspace_id`` may be
  // ``undefined`` on a tenant-wide token — pass through as-is.
  const fetcher = useCallback(
    () => DashboardService.getMetrics(user?.workspace_id ?? undefined),
    [user?.workspace_id]
  );

  const { data: metrics, isLoading, refetch } = useApi(fetcher, {
    deps: [user?.workspace_id],
  });

  // Light polling so the cards stay current without dragging in a
  // dedicated state manager.
  usePoll(refetch, POLL_INTERVAL_MS, { enabled: !!user });

  const sourceStatus = metrics?.source_status;

  return (
    <div className="space-y-8">
      <DashboardGreeting greeting={greeting} />

      <QuickActions />

      <div className="grid grid-cols-1 gap-4 tablet:grid-cols-2 desktop:grid-cols-3">
        <GovernancePostureCard
          value={metrics?.governance_posture_pct ?? null}
          band={metrics?.governance_posture_band ?? null}
          isLoading={isLoading && !metrics}
          isError={sourceStatus?.posture === "error"}
        />
        <WorkspacesCard
          value={metrics?.active_workspaces_count ?? 0}
          isLoading={isLoading && !metrics}
          isError={sourceStatus?.workspaces === "error"}
        />
        <RequestsCard
          value={metrics?.total_requests_24h ?? 0}
          isLoading={isLoading && !metrics}
          isError={sourceStatus?.requests === "error"}
        />
        <PolicyDecisionsCard
          value={metrics?.policy_decisions_count_24h ?? 0}
          isLoading={isLoading && !metrics}
          isError={sourceStatus?.policy === "error"}
        />
        <RiskScoreCard
          value={metrics?.risk_score_overall ?? null}
          band={metrics?.risk_score_band ?? null}
          isLoading={isLoading && !metrics}
          isError={sourceStatus?.risk === "error"}
        />
        {/* Spacer to align the 5 number tiles cleanly into the
            tablet 2-col / desktop 3-col grids. Hidden on mobile
            where everything stacks. */}
        <div className="hidden tablet:block" aria-hidden="true" />

        <RecentActivityCard
          items={metrics?.recent_activity ?? []}
          isLoading={isLoading && !metrics}
          isError={sourceStatus?.activity === "error"}
        />
      </div>
    </div>
  );
}
